import { ROUTES } from 'utils/constants/routes';
import { SELFIE_ERROR_MESSAGES } from 'utils/constants';
import { resetFlow } from 'features/govtId/v2/state/slice';
import { selectSelfieFailureReason } from 'features/govtId/v2/state/selectors';
import { selectVerificationDetails } from 'store/common/selectors/profile';

/**
 * The screens a user sees after a document or selfie failure — the error leaves, the session
 * timeout, the sides-failed screen — live under `/govtid` and serve the Regula (v2) flow. This module
 * is the one place that says where those screens send the user and what they reset.
 */
export const FLOW_CHROME = {
    entryPath: ROUTES.GOVT_ID_V2,
    selfieEntryPath: ROUTES.GOVT_ID_V2_CAPTURE_SELFIE,
    resetActions: [resetFlow],
    // The recheck CTA compares an OCR document type against a classification, neither of which this
    // flow produces.
    supportsRecheckCta: false,
    // A retry always begins a new transaction, so a side cannot be replaced inside the current one.
    allowsSingleSideRetry: false,
};

/**
 * Where a retry from a shared screen goes. Some of those screens serve both halves of the flow —
 * a refused camera and a technical failure are reachable from the document capture and from the
 * selfie — so the destination follows how far the user has actually got: a document that has already
 * passed is not something they can usefully re-shoot.
 */
export const selectRetryPathForFlow = (s) =>
    selectVerificationDetails(s)?.is_document_valid
        ? FLOW_CHROME.selfieEntryPath
        : FLOW_CHROME.entryPath;

// Attempts are counted from the verification response alone.
export const selectDocumentAttemptsCountForFlow = (s) =>
    selectVerificationDetails(s)?.govt_id_attempts ?? 0;

// The selfie failure is stored as a code, not a sentence, so the copy is resolved here. Anything
// unrecognized — including a failure recorded before a reason was — falls back to the generic case
// rather than showing the user nothing.
export const selectSelfieFailureMessageForFlow = (s) =>
    SELFIE_ERROR_MESSAGES[selectSelfieFailureReason(s)] ?? SELFIE_ERROR_MESSAGES.DEFAULT;
